import { useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useAuthStore } from '../store/useAuthStore';
import { useBusinessStore } from '../store/useBusinessStore';
import { getStoredFeatureOverrides } from '../utils/businessConfigHelpers';
import { normalizeBusiness } from '../utils/normalizeBusiness';
import { getStoredAdminEmails } from '../utils/adminConstants';

export const useAppInitialization = () => {
  const { setAuth, setIsCheckingAuth, logout } = useAuthStore();
  const { setMasterDb, setPlansDb, setActiveBusinessId } = useBusinessStore();

  useEffect(() => {
    let subscription = null;

    const checkAuth = async () => {
      setIsCheckingAuth(true);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          await processUserSession(session);
        } else {
          // Check if mock/local auth exists in store or leave as unauthenticated
          setIsCheckingAuth(false);
        }
      } catch (e) {
        console.error("Error checking session:", e);
      } finally {
        setIsCheckingAuth(false);
      }
    };

    const processUserSession = async (session) => {
      const userEmail = session.user.email?.toLowerCase().trim();
      const adminEmails = getStoredAdminEmails().map(e => e.toLowerCase().trim());

      const isAdmin = adminEmails.includes(userEmail);

      try {
        let query = supabase.from('businesses').select('*, staff(*), services(*), appointments(*), orders(*)');
        if (!isAdmin) {
          query = query.eq('google_calendar_id', userEmail);
        }

        const [{ data: bData, error: bErr }, { data: pData, error: pErr }] = await Promise.all([
          query,
          supabase.from('plans').select('*')
        ]);

        if (!isAdmin && bData && bData.length > 0 && !bData.some(b => b.activo !== false)) {
          await supabase.auth.signOut();
          logout();
          return;
        }

        if (isAdmin) {
          setAuth(true, userEmail, 'master');
        } else {
          setAuth(true, userEmail, 'client');
        }

        if (!bErr && bData) {
          const overrides = getStoredFeatureOverrides();
          const normalized = bData.map(b => normalizeBusiness(b, overrides));

          setMasterDb(normalized);

          // Set active business ID if it's a client and they have one, or if it's master to have a default context
          if (normalized.length > 0) {
            const currentActiveId = useBusinessStore.getState().activeBusinessId;
            const idExists = normalized.some(b => String(b.id) === String(currentActiveId));
            if (!currentActiveId || !idExists) {
              setActiveBusinessId(String(normalized[0].id));
            }
          }
        }
        if (!pErr && pData) {
          setPlansDb(pData);
        }
      } catch (err) {
        console.error("Error fetching businesses on init:", err);
      }
    };

    checkAuth();

    // Suscripción a cambios
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session) {
        processUserSession(session);
      } else if (event === 'SIGNED_OUT') {
        logout();
      }
    });
    subscription = authListener?.subscription;

    return () => {
      if (subscription) subscription.unsubscribe();
    };
  }, [setAuth, setIsCheckingAuth, logout]);
};
